import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Delete } from "lucide-react";

/**
 * Mobile-only custom numeric keypad rendered as a bottom sheet.
 * Replaces the OS keyboard for temperature / measurement entry so we can
 * expose a "-" key (which Android & iOS numeric/decimal keyboards omit).
 *
 * Behaviour:
 *  - `1-9 0 .`  → append (single decimal point enforced)
 *  - `-`        → toggle sign of the current value
 *  - `⌫`        → backspace
 *  - `✓ Done`   → close the sheet
 *
 * The host input should be `readOnly` while this is open so the OS
 * keyboard does not also pop up. Render this component only on screens
 * below the `md` breakpoint (see `useIsMobile`).
 */
export default function MobileNumpad({ value, onChange, onClose, unit = "" }) {
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const append = (ch) => {
    if (ch === ".") {
      if (value.includes(".")) return;
      onChange(value === "" || value === "-" ? value + "0." : value + ".");
      return;
    }
    onChange((value || "") + ch);
  };
  const toggleSign = () => onChange(value.startsWith("-") ? value.slice(1) : "-" + value);
  const backspace = () => onChange(value.slice(0, -1));

  const KEYS = [
    "1", "2", "3",
    "4", "5", "6",
    "7", "8", "9",
    "-", "0", ".",
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex flex-col justify-end"
      style={{ background: "rgba(0,0,0,0.15)" }}
      onClick={onClose}
      data-testid="mobile-numpad-overlay"
    >
      <div
        className="rounded-t-2xl p-2.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-2xl"
        style={{ background: "var(--vb-bg, #fff)" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Numeric keypad"
        data-testid="mobile-numpad"
      >
        {/* Header: live value preview */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
            Enter value
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold px-2 py-0.5 rounded"
            style={{ color: "var(--vb-accent, #1e90ff)" }}
            data-testid="mobile-numpad-done"
          >
            <span className="inline-flex items-center gap-1"><Check size={14} /> Done</span>
          </button>
        </div>

        <div
          className="mb-2 rounded-md px-2 py-1 text-base font-mono text-right tabular-nums"
          style={{ background: "var(--vb-bg-2, #f3f4f6)", color: "var(--vb-text-1)", minHeight: 30 }}
          data-testid="mobile-numpad-display"
        >
          {value || "0"}{unit && <span className="ml-1 text-xs opacity-60">{unit}</span>}
        </div>

        {/* 3-col key grid */}
        <div className="grid grid-cols-3 gap-1.5">
          {KEYS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => (k === "-" ? toggleSign() : append(k))}
              className="py-2.5 text-lg font-semibold rounded-md active:opacity-70 select-none"
              style={{
                background: k === "-" ? "var(--vb-bg-2, #f3f4f6)" : "var(--vb-bg, #fff)",
                border: "1px solid var(--vb-border, rgba(0,0,0,0.08))",
                color: "var(--vb-text-1)",
              }}
              data-testid={`mobile-numpad-key-${k === "-" ? "minus" : k === "." ? "dot" : k}`}
            >
              {k === "-" ? "−" : k}
            </button>
          ))}
          {/* Last row: backspace spans 3 columns */}
          <button
            type="button"
            onClick={backspace}
            className="col-span-3 py-2 text-xs font-semibold rounded-md active:opacity-70 flex items-center justify-center gap-1.5"
            style={{
              background: "var(--vb-bg-2, #f3f4f6)",
              border: "1px solid var(--vb-border, rgba(0,0,0,0.08))",
              color: "var(--vb-text-1)",
            }}
            data-testid="mobile-numpad-backspace"
            aria-label="Delete"
          >
            <Delete size={14} /> Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Tiny hook — returns `true` when the viewport is below the Tailwind `md`
 * breakpoint (768px). Used to decide whether to mount the custom keypad
 * and lock the input to `readOnly`.
 */
export function useIsMobile() {
  // SSR-safe initial value (matches what the server would emit). On the
  // client this resolves on first effect tick.
  const [m, setM] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767.98px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767.98px)");
    const fn = (e) => setM(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", fn);
    else mq.addListener(fn);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", fn);
      else mq.removeListener(fn);
    };
  }, []);
  return m;
}
